import { useState } from "react";
import type { HrCertificate, HrPerson, SalaryPerson } from "@dentacore/shared-types";

import { money } from "../accounting/format";
import { Pager, useFitPages } from "../ui/paging";
import { daysText, hoursOf, initialsOf, roleLabel } from "./roster";
import styles from "./staff.module.css";
import { CONTRACT_LABELS, daysBetween, personalFacts, salaryToday, shortDate } from "./team";
import type { Tone } from "./team";
import own from "./team.module.css";
import { Mark } from "./team-mark";

type PanelTab = "contract" | "personal" | "time" | "certificates" | "pay";

const TODAY_WORDS: Record<HrPerson["today"], string> = {
  IN: "in today",
  OFF: "off today",
  ON_LEAVE: "on leave today",
  SICK: "off sick today",
};

/**
 * One person's HR record, beside the team (STORY-181c). **Four short tabs
 * rather than one long one**, as the prototype settled: each holds one kind of
 * thing, and the foot carries that tab's action or its pager — always drawn,
 * so the tab's room never changes when a pager appears.
 */
export function TeamPanel({
  person,
  today,
  isSelf,
  canManageContract,
  canRecordAbsence,
  canRenew,
  pay,
  currency,
  busy,
  onClose,
  onChangeContract,
  onChangePersonal,
  onSick,
  onBackAtWork,
  onRenew,
}: {
  person: HrPerson;
  today: string;
  /** Their own record — nobody records their own sickness (HR-D8). */
  isSelf: boolean;
  canManageContract: boolean;
  canRecordAbsence: boolean;
  canRenew: boolean;
  /** Their salaries, for `accounting.salaries.view` (QA4-D8); `undefined` hides the Pay tab. */
  pay: SalaryPerson | null | undefined;
  currency: string;
  busy: boolean;
  onClose: () => void;
  onChangeContract: () => void;
  onChangePersonal: () => void;
  onSick: () => void;
  onBackAtWork: () => void;
  onRenew: (certificate: HrCertificate) => void;
}): React.JSX.Element {
  const tabs: { key: PanelTab; label: string }[] = [
    { key: "contract", label: "Contract" },
    { key: "personal", label: "Personal" },
    { key: "time", label: "Time" },
    { key: "certificates", label: "Certificates" },
    ...(pay === undefined ? [] : [{ key: "pay" as const, label: "Pay" }]),
  ];
  const [tab, setTab] = useState<PanelTab>("contract");
  const shown = tabs.some((row) => row.key === tab) ? tab : "contract";
  const [first = "", ...rest] = person.name.split(" ");

  return (
    <aside className={own.panel} aria-label={person.name} data-testid="team-panel">
      <div className={styles.band} data-role={person.role}>
        <span className={styles.avatar} aria-hidden="true">
          {initialsOf(first, rest.join(" "))}
        </span>
        <div className={styles.bandText}>
          <h2 className={styles.bandName}>{person.name}</h2>
          <span className={styles.bandSub}>
            <span className={styles.bandRole}>{roleLabel(person.role)}</span>
            {" · "}
            <span data-testid="team-panel-today">{TODAY_WORDS[person.today]}</span>
          </span>
        </div>
        <button
          type="button"
          className={styles.close}
          aria-label="Close"
          onClick={onClose}
          data-testid="team-panel-close"
        >
          &times;
        </button>
      </div>

      <div className={own.panelTabs} role="tablist" aria-label={`${person.name}: record`}>
        {tabs.map((row) => (
          <button
            key={row.key}
            type="button"
            role="tab"
            aria-selected={shown === row.key}
            tabIndex={shown === row.key ? 0 : -1}
            className={shown === row.key ? `${styles.tab} ${styles.tabOn}` : styles.tab}
            onClick={() => setTab(row.key)}
            data-testid={`team-tab-${row.key}`}
          >
            {row.label}
          </button>
        ))}
      </div>

      {shown === "contract" && (
        <>
          <div className={own.panelBody}>
            <ContractFacts person={person} today={today} />
          </div>
          <div className={own.foot}>
            {canManageContract ? (
              <button
                type="button"
                className={styles.ghost}
                disabled={busy}
                onClick={onChangeContract}
                data-testid="change-contract"
              >
                Change contract
              </button>
            ) : (
              <span className={own.footNote}>Only somebody who manages records changes it.</span>
            )}
          </div>
        </>
      )}

      {shown === "personal" && (
        <>
          <div className={own.panelBody}>
            <Facts facts={personalFacts(person, today)} testId="personal-facts" />
          </div>
          <div className={own.foot}>
            {canManageContract ? (
              <button
                type="button"
                className={styles.ghost}
                disabled={busy}
                onClick={onChangePersonal}
                data-testid="change-personal"
              >
                Change personal details
              </button>
            ) : (
              <span className={own.footNote}>Only somebody who manages records changes it.</span>
            )}
          </div>
        </>
      )}

      {shown === "time" && (
        <>
          <div className={own.panelBody}>
            <TimeMeters person={person} />
          </div>
          <div className={own.foot}>
            {isSelf ? (
              <span className={own.footNote}>
                Nobody records their own sickness — ask a colleague.
              </span>
            ) : !canRecordAbsence ? (
              <span className={own.footNote}>Recording sickness is a manager&rsquo;s.</span>
            ) : person.today === "SICK" ? (
              <>
                <span className={own.footNote}>Off sick today.</span>
                <button
                  type="button"
                  className={styles.ghost}
                  disabled={busy}
                  onClick={onBackAtWork}
                  data-testid="back-at-work"
                >
                  Back at work
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.ghost}
                disabled={busy || person.today === "ON_LEAVE"}
                title={person.today === "ON_LEAVE" ? "They are on leave today" : undefined}
                onClick={onSick}
                data-testid="record-sick"
              >
                Record off sick
              </button>
            )}
          </div>
        </>
      )}

      {shown === "certificates" && (
        <CertificateList
          person={person}
          today={today}
          canRenew={canRenew}
          busy={busy}
          onRenew={onRenew}
        />
      )}

      {shown === "pay" && (
        <>
          <div className={own.panelBody}>
            <PayFacts pay={pay ?? null} today={today} currency={currency} />
          </div>
          <div className={own.foot}>
            <span className={own.footNote}>Set on Accounting → Salaries.</span>
          </div>
        </>
      )}
    </aside>
  );
}

function ContractFacts({ person, today }: { person: HrPerson; today: string }): React.JSX.Element {
  const { contract } = person;
  const type = contract.contractType === null ? "Not set" : CONTRACT_LABELS[contract.contractType];
  const facts: [string, string][] = [
    [
      "Contract",
      contract.contractedMinutes === null
        ? type
        : `${type} · ${hoursOf(contract.contractedMinutes)} a week`,
    ],
    ["Started", contract.startedOn === null ? "—" : shortDate(contract.startedOn)],
    [
      "Probation",
      contract.probationEndsOn === null
        ? "—"
        : contract.probationEndsOn < today
          ? `Passed · ${shortDate(contract.probationEndsOn)}`
          : `Ends ${shortDate(contract.probationEndsOn)}`,
    ],
    [
      "Notice",
      contract.noticeWeeks === null
        ? "—"
        : `${String(contract.noticeWeeks)} ${contract.noticeWeeks === 1 ? "week" : "weeks"}`,
    ],
    ["Last day", contract.endedOn === null ? "—" : shortDate(contract.endedOn)],
    ["Works at", person.locations.map((row) => row.name).join(", ") || "—"],
  ];
  return <Facts facts={facts} testId="contract-facts" />;
}

function Facts({
  facts,
  testId,
}: {
  facts: [string, string][];
  testId: string;
}): React.JSX.Element {
  return (
    <dl className={own.facts} data-testid={testId}>
      {facts.map(([label, value]) => (
        <div key={label} className={own.fact}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function TimeMeters({ person }: { person: HrPerson }): React.JSX.Element {
  const { leave, contract } = person;
  const allowance = leave.entitlementDays;
  const used = leave.takenDays + leave.approvedDays;
  const over = leave.remainingDays !== null && leave.remainingDays < 0;
  return (
    <div className={own.meters}>
      <div className={own.meter}>
        <div className={own.meterLine}>
          <span>Contracted</span>
          <span>
            {contract.contractedMinutes === null
              ? "No hours set"
              : `${hoursOf(contract.contractedMinutes)} a week`}
          </span>
        </div>
      </div>
      <div className={own.meter} data-testid="leave-meter">
        <div className={own.meterLine}>
          <span>Annual leave left, {leave.year}</span>
          <span className={over ? own.warmText : undefined}>
            {allowance === null || leave.remainingDays === null
              ? "No allowance set"
              : over
                ? `${daysText(-leave.remainingDays)} over the allowance`
                : `${daysText(leave.remainingDays)} of ${daysText(allowance)} days`}
          </span>
        </div>
        {allowance !== null && allowance > 0 && (
          <span className={over ? `${own.bar} ${own.barWarm}` : own.bar} aria-hidden="true">
            <i
              style={{ width: `${String(Math.min(100, Math.round((used / allowance) * 100)))}%` }}
            />
          </span>
        )}
        <span className={own.sub}>
          {daysText(leave.takenDays)} taken · {daysText(leave.approvedDays)} booked ·{" "}
          {daysText(leave.pendingDays)} waiting
        </span>
      </div>
    </div>
  );
}

function CertificateList({
  person,
  today,
  canRenew,
  busy,
  onRenew,
}: {
  person: HrPerson;
  today: string;
  canRenew: boolean;
  busy: boolean;
  onRenew: (certificate: HrCertificate) => void;
}): React.JSX.Element {
  const rows = person.certificates;
  const fit = useFitPages({
    keys: rows.map((row) => row.id),
    estimate: () => 57,
    gap: 0,
    resetOn: person.userId,
  });
  const [start, end] = fit.range;

  return (
    <>
      <div className={own.panelBody} ref={fit.areaRef}>
        {rows.length === 0 ? (
          <p className={own.empty}>No certificates recorded. They are added on Compliance.</p>
        ) : (
          <div className={own.certs} data-testid="team-certificates">
            {rows.slice(start, end).map((row) => (
              <div
                key={row.id}
                className={own.cert}
                ref={fit.measure(row.id)}
                data-testid="team-certificate"
              >
                <Mark tone={toneOfCertificate(row)} />
                <span className={own.certText}>
                  <b>{row.name}</b>
                  <span className={own.sub}>{certificateLine(row, today)}</span>
                </span>
                {canRenew && (
                  <button
                    type="button"
                    className={styles.quiet}
                    disabled={busy}
                    onClick={() => onRenew(row)}
                    aria-label={`Renew ${person.name}'s ${row.name}`}
                    data-testid="renew-open"
                  >
                    Renew
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={own.foot}>
        <Pager
          page={fit.page}
          count={fit.pages.length}
          onPage={fit.setPage}
          from={start + 1}
          to={end}
          total={rows.length}
          noun="certificates"
          compact
          testId="team-certificates-pager"
        />
      </div>
    </>
  );
}

function toneOfCertificate(row: HrCertificate): Tone {
  return row.state === "EXPIRED" ? "bad" : row.state === "DUE" ? "warm" : "calm";
}

/** `Expired 3 days ago · 9 Oct 2026`, `8 days · 20 Oct 2026`, or `Does not run out`. */
function certificateLine(row: HrCertificate, today: string): string {
  const parts: string[] = [];
  if (row.expiresOn === null) parts.push("Does not run out");
  else {
    const ahead = daysBetween(today, row.expiresOn);
    parts.push(
      ahead < 0
        ? `Expired ${String(-ahead)} ${ahead === -1 ? "day" : "days"} ago${row.neededToTreat ? " · still bookable" : ""}`
        : `${String(ahead)} ${ahead === 1 ? "day" : "days"} · ${shortDate(row.expiresOn)}`,
    );
  }
  if (row.pendingRenewal !== null) parts.push("renewal waiting for a check");
  if (row.rejection !== null) parts.push("last upload rejected");
  return parts.join(" · ");
}

function PayFacts({
  pay,
  today,
  currency,
}: {
  pay: SalaryPerson | null;
  today: string;
  currency: string;
}): React.JSX.Element {
  const current = pay === null ? null : salaryToday(pay.salaries, today);
  if (pay === null || current === null) {
    return <p className={own.empty}>No salary recorded.</p>;
  }
  return (
    <dl className={own.facts} data-testid="pay-facts">
      <div className={own.fact}>
        <dt>Salary</dt>
        <dd>{money(current.monthlyCents, currency)} a month</dd>
      </div>
      <div className={own.fact}>
        <dt>Since</dt>
        <dd>{shortDate(current.effectiveFrom)}</dd>
      </div>
      <div className={own.fact}>
        <dt>This month so far</dt>
        <dd>{money(pay.thisMonth.cents, currency)}</dd>
      </div>
    </dl>
  );
}
